import type { Alarm, Case, FMEAUpdateReview, RecommendedAction } from '@/versions/v1/schema/case';
import type { PlantRoleId } from '@/versions/v1/schema/plant-role';
import type { ControlLoopState } from '@/versions/v1/schema/advisory-issue';
import type { Persona } from '@/shared/types';
import type { PillProps } from '@/design-system/Pill';
import type { TrendReference, TrendSeries, VizTone } from '@/versions/v1/modules/_viz';
import {
  ALARMS,
  CASE_OUTCOMES,
  FAILURE_MODES,
  VARIATION_EVENTS,
} from '@/versions/v1/shared/bunker-hill-advisory-seed';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import {
  clockOnProductionDay,
  diffMinutes,
  formatElapsedMinutes,
  isoDateFromRaisedAt,
  isCaseThisMonth,
  isCaseThisShift,
  parseTime,
  productionDayOf,
  shiftOf,
} from '@/versions/v1/shared/clock.derive';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { SUBPROCESSES } from '@/versions/v1/shared/bunker-hill-seed';
import { recoveryForecastRead } from '@/versions/v1/modules/overview/RecoveryTrend.transform';
import { recoveryTrend } from '@/versions/v1/modules/overview/Performance.transform';
import { parseDwellHours } from '@/versions/v1/modules/process/Scene.transform';
import { conditionMonitoringPoints } from '@/versions/v1/modules/maintenance/Scene.transform';
import { workOrderRows } from '@/versions/v1/modules/maintenance/Scene.transform';
import { applyRecommendedActions, type AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import type { IssueSeverity } from '@/versions/v1/schema/advisory-issue';
import type { Diagnosis } from '@/versions/v1/shared/DiagnosisEngine';
import { alarmCoveredVariableIds } from '@/versions/v1/shared/DiagnosisEngine';
import { excursions } from '@/versions/v1/shared/SpcEngine';
import { MONITORED_VARIABLES } from '@/versions/v1/shared/bunker-hill-spc';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import {
  rankExcursions,
  dedupeRankedExcursions,
  topN,
  reasonChipLabel,
  workbookPriorityChipLabel,
} from '@/versions/v1/shared/alertGovernance';
import { constraintChipForAction } from '@/versions/v1/shared/constraint.transform';
import { correlationLineForCase } from '@/versions/v1/shared/correlation.transform';
import { metPatternsPanel } from '@/versions/v1/shared/patterns.transform';
import { RECOMMENDED_ACTIONS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { perturbedSeriesIds } from '@/versions/v1/shared/perturbations';
import { mergeCasesForThreading, threadsFor } from '@/versions/v1/shared/threads';
import {
  casesForRole,
  CROSS_RUNG_CASE_IDS,
  superintendentResourcingAsk,
} from '@/versions/v1/shared/role-scope';
import { isRuleLive } from '@/versions/v1/shared/AdvisoryEngine';
import { liveRuleSet } from '@/versions/v1/shared/RuleSetEngine';
import { ZERO_TOUCH } from './branch-labels';
import {
  enrichFmeaReview,
  formatReviewRoutedTo,
  fmeaOccurrenceLabel,
  reviewsForCase,
  severityChip,
  STATE_CHIP,
  ASSIGNEE_SHORT,
} from './Scene.transform';
import { matchedRuleOutcomeLabel } from './CaseDrawer.transform';
import { rulingStateLabel } from './Approvals.transform';
import { displayCaseTitle } from '@/versions/v1/shared/recovery-vocab.derive';

export type RoleScreenId = 'cro' | 'met' | 'maint' | 'super' | 'manager';

export type ScopeSubprocessId =
  | 'sp-crush'
  | 'sp-grind'
  | 'sp-pb'
  | 'sp-zn'
  | 'sp-filter'
  | 'sp-paste'
  | 'sp-water';

export const VALID_SCOPE_IDS: readonly ScopeSubprocessId[] = [
  'sp-crush',
  'sp-grind',
  'sp-pb',
  'sp-zn',
  'sp-filter',
  'sp-paste',
  'sp-water',
];

export const OPEN_CASE_STATES: readonly Case['state'][] = [
  'detected',
  'matched',
  'routed',
  'evidence-requested',
  'escalated',
  'diagnosed',
  'outcome-documented',
  'fmea-review',
];

export const CLOSED_CASE_STATES: readonly Case['state'][] = ['approved', 'rejected', 'closed'];

export type RoleScreenKpi = {
  label: string;
  value: string;
  caption: string;
  tone: VizTone;
  provenance?: string;
  provenanceChip?: string;
  /** When set, the tile opens the first case in the drawer. */
  linkCaseIds?: readonly string[];
};

export type RoleScreenStatusChip = {
  label: string;
  tone: PillProps['tone'];
  title?: string;
};

export type RoleScreenPrimaryItem = {
  caseId: string;
  reviewId?: string;
  title: string;
  subtitle?: string;
  correlationLine?: string;
  detailLines?: { label: string; text: string }[];
  /** Superintendent management-queue columns (R5). */
  ageLabel?: string;
  shiftRaised?: string;
  resourcingAsk?: string;
  statusChips: RoleScreenStatusChip[];
  group?: string;
  threadHead?: {
    rootLabel: string;
    signalCount: number;
    yoursCount: number;
    worstSeverity: IssueSeverity;
  };
  indent?: boolean;
  quiet?: boolean;
  threadGroupId?: string;
  severity?: IssueSeverity;
};

export const PRIMARY_LIST_UNIT_CAP = 6;

type PrimaryListUnit = {
  severity: IssueSeverity;
  isThread: boolean;
  items: RoleScreenPrimaryItem[];
};

const QUEUE_SEVERITY_RANK: Record<IssueSeverity, number> = {
  high: 0,
  med: 1,
  low: 2,
  monitor: 3,
};

const QUIET_ASSIGNEE_LABEL: Partial<Record<PlantRoleId, string>> = {
  gm: 'GM',
  superintendent: 'Supt',
};

const SEVERITY_PILL_TONE: Record<IssueSeverity, PillProps['tone']> = {
  high: 'danger',
  med: 'warning',
  low: 'default',
  monitor: 'default',
};

/** The severity pill's tone, as on the case page header: only high and med carry a state wash. */
export function severityPillTone(severity: IssueSeverity): PillProps['tone'] {
  return SEVERITY_PILL_TONE[severity];
}

export function primaryListUnits(items: readonly RoleScreenPrimaryItem[]): PrimaryListUnit[] {
  const units: PrimaryListUnit[] = [];
  let index = 0;
  while (index < items.length) {
    const item = items[index]!;
    if (item.threadHead) {
      const unitItems = [item];
      index += 1;
      while (index < items.length && items[index]?.threadGroupId === item.threadGroupId) {
        unitItems.push(items[index]!);
        index += 1;
      }
      units.push({
        severity: item.threadHead.worstSeverity,
        isThread: true,
        items: unitItems,
      });
      continue;
    }
    units.push({
      severity: item.severity ?? 'monitor',
      isThread: false,
      items: [item],
    });
    index += 1;
  }
  return units;
}

export function visiblePrimaryListItems(
  items: readonly RoleScreenPrimaryItem[],
  unitCap = PRIMARY_LIST_UNIT_CAP,
): RoleScreenPrimaryItem[] {
  const units = primaryListUnits(items);
  const sorted = sortQueueUnits(units);
  return sorted.slice(0, unitCap).flatMap((unit) => unit.items);
}

function sortQueueUnits(units: PrimaryListUnit[]): PrimaryListUnit[] {
  return units.slice().sort((left, right) => {
    const severityDelta =
      QUEUE_SEVERITY_RANK[left.severity] - QUEUE_SEVERITY_RANK[right.severity];
    if (severityDelta !== 0) return severityDelta;
    if (left.isThread !== right.isThread) return left.isThread ? -1 : 1;
    return 0;
  });
}

export type RoleScreenLoopRow = {
  tag: string;
  name: string;
  modeLabel: string;
  note: string;
  tone: PillProps['tone'];
};

export type RoleScreenFmeaRow = {
  reviewId: string;
  summary: string;
  caseCode?: string;
  routedTo: string;
  rulingStateLabel: string;
  occurrence: string;
  occurrenceTitle?: string;
};

export type RoleScreenCallout = {
  title: string;
  body: string;
  tone: VizTone;
};

export type RoleScreenSignOffRow = {
  reviewId: string;
  summary: string;
  caseTitle?: string;
  caseId?: string;
  reviewedBy?: string;
  approvalStatus: string;
};

export type RoleScreenSecondaryPanel =
  | {
      id: string;
      kind: 'loops';
      title: string;
      loops: RoleScreenLoopRow[];
    }
  | {
      id: string;
      kind: 'fmea-queue';
      title: string;
      rows: RoleScreenFmeaRow[];
      /** The reviews waiting on the reader's own seat (`seniorMetReviewsAwaiting`). */
      yoursCount?: number;
    }
  | {
      id: string;
      kind: 'recovery-trend';
      title: string;
      provenance?: string;
      provenanceChip?: string;
    }
  | {
      id: string;
      kind: 'work-order';
      title: string;
      asset: string;
      description: string;
      wo: string;
      caseCode?: string;
      provenance: string;
      provenanceChip: string;
    }
  | {
      id: string;
      kind: 'recently-closed';
      title: string;
      caseIds: string[];
    }
  | {
      id: string;
      kind: 'review-seat';
      title: string;
      body: string;
    }
  | {
      id: string;
      kind: 'callouts';
      title: string;
      callouts: RoleScreenCallout[];
    }
  | {
      id: string;
      kind: 'sign-off';
      title: string;
      rows: RoleScreenSignOffRow[];
    }
  | {
      id: string;
      kind: 'ranked-excursions';
      title: string;
      shown: {
        excursionId: string;
        label: string;
        score: number;
        chips: string[];
      }[];
      more: {
        excursionId: string;
        label: string;
        score: number;
        chips: string[];
      }[];
      moreCount: number;
    }
  | {
      id: string;
      kind: 'learning-trend';
      title: string;
      caption: string;
      points: readonly { label: string; value: number }[];
      provenance: string;
    }
  | {
      id: string;
      kind: 'patterns';
      title: string;
      /** One line for every scoped variable with no pattern at the cursor: `Stable · <label> · <value> · …`. */
      stableLine?: string;
      rows: {
        label: string;
        pattern: string;
        valueLine?: string;
        causes: { prose: string; ruleId: string }[];
      }[];
    };

export type RoleScreenViewModel = {
  screenId: RoleScreenId;
  badge: string;
  badgeTone: PillProps['tone'];
  title: string;
  question: string;
  scopeLine: string;
  scopeId: ScopeSubprocessId | null;
  cadenceLine: string;
  kpis: [RoleScreenKpi, RoleScreenKpi, RoleScreenKpi, RoleScreenKpi];
  primary: {
    title: string;
    items: RoleScreenPrimaryItem[];
    emptyMessage: string;
    forecastLine?: string;
  };
  secondary: RoleScreenSecondaryPanel[];
  footer: string;
};

export type RoleScreenInput = {
  cases: readonly Case[];
  reviews: readonly FMEAUpdateReview[];
  loops: readonly ControlLoopState[];
  actions: readonly RecommendedAction[];
  alarms: readonly Alarm[];
  scope?: string | null;
  sessionState: SessionState;
  advisory: AdvisorySessionState;
  diagnosis: Diagnosis;
};

const SCREEN_META: Record<
  RoleScreenId,
  { badge: string; title: string; question: string; cadence: string; badgeTone: PillProps['tone'] }
> = {
  cro: {
    badge: 'Control Room',
    title: 'Live advisory queue',
    question: 'What do I do about a case in the next five minutes?',
    cadence: 'Real-time · tied to DCS tag state',
    badgeTone: 'default',
  },
  met: {
    badge: 'Senior Metallurgist',
    title: 'Case queue & diagnosis',
    question: 'Which cases need full diagnosis and an FMEA row proposal?',
    cadence: 'Day shift · Grinding / Flotation panel',
    badgeTone: 'admin',
  },
  maint: {
    badge: 'Maintenance Supervisor',
    title: 'Mechanical / electrical case queue',
    question: 'What mechanical or electrical cases need action on the floor?',
    cadence: 'Day shift · plant-wide',
    badgeTone: 'warning',
  },
  super: {
    badge: 'Operations Superintendent',
    title: 'Shift case rollup',
    question: 'What open cases across every area need a shift decision?',
    cadence: 'Day shift · all areas',
    badgeTone: 'success',
  },
  manager: {
    badge: 'Concentrator Manager',
    title: 'Performance & learning-loop trend',
    question: 'Is the FMEA growing responsibly — and what awaits sign-off?',
    cadence: 'Month 4 · plant-wide',
    badgeTone: 'admin',
  },
};

export function roleScreenForPersona(persona: Persona): RoleScreenId {
  switch (persona) {
    case 'Control room':
    case 'Operator':
      return 'cro';
    case 'Metallurgist':
    case 'Senior metallurgist':
      return 'met';
    case 'Maintenance':
      return 'maint';
    case 'Ops supervisor':
    case 'Superintendent':
      return 'super';
    case 'GM':
      return 'manager';
    case 'Admin':
    case 'Editor':
    case 'Reviewer':
    case 'Viewer':
    default:
      return 'super';
  }
}

export function parseScopeParam(raw: string | null): ScopeSubprocessId | null {
  if (!raw) return null;
  return VALID_SCOPE_IDS.includes(raw as ScopeSubprocessId)
    ? (raw as ScopeSubprocessId)
    : null;
}

export function scopeLabel(scopeId: ScopeSubprocessId): string {
  const subprocess = SUBPROCESSES.find((row) => row.id === scopeId);
  if (!subprocess) return scopeId;
  if (scopeId === 'sp-grind') return 'Grinding circuit';
  if (scopeId === 'sp-pb') return 'Pb flotation circuit';
  if (scopeId === 'sp-zn') return 'Zn flotation circuit';
  if (scopeId === 'sp-crush') return 'Crushing circuit';
  if (scopeId === 'sp-filter') return 'Filtering circuit';
  if (scopeId === 'sp-paste') return 'Paste backfill';
  if (scopeId === 'sp-water') return 'Process water';
  return subprocess.name;
}

export function caseSubprocessId(caseItem: Case): ScopeSubprocessId | null {
  if (caseItem.variationEventId) {
    const event = VARIATION_EVENTS.find((row) => row.id === caseItem.variationEventId);
    if (event && VALID_SCOPE_IDS.includes(event.subprocessId as ScopeSubprocessId)) {
      return event.subprocessId as ScopeSubprocessId;
    }
  }
  const circuit = caseItem.circuit.toLowerCase();
  if (circuit.includes('crush')) return 'sp-crush';
  if (circuit.includes('pb') || circuit.includes('ag')) return 'sp-pb';
  if (circuit.includes('zn')) return 'sp-zn';
  if (circuit.includes('grind') || circuit.includes('ball mill') || circuit.includes('cyclone')) {
    return 'sp-grind';
  }
  if (circuit.includes('filter') || circuit.includes('thick')) return 'sp-filter';
  if (circuit.includes('paste') || circuit.includes('tail')) return 'sp-paste';
  if (circuit.includes('water')) return 'sp-water';
  return null;
}

export function filterCasesByScope(
  cases: readonly Case[],
  scopeId: ScopeSubprocessId | null,
): Case[] {
  if (!scopeId) return cases.slice();
  return cases.filter((caseItem) => caseSubprocessId(caseItem) === scopeId);
}

function isOpen(caseItem: Case): boolean {
  return OPEN_CASE_STATES.includes(caseItem.state);
}

function isThisShift(caseItem: Case, cursor: SeriesTime): boolean {
  return isCaseThisShift(caseItem.raisedAt, cursor);
}

const CASE_MONTH_NUM: Record<string, string> = {
  Jan: '01',
  Feb: '02',
  Mar: '03',
  Apr: '04',
  May: '05',
  Jun: '06',
  Jul: '07',
  Aug: '08',
  Sep: '09',
  Oct: '10',
  Nov: '11',
  Dec: '12',
};

function caseTimestampMs(at: string, cursor: SeriesTime): number | null {
  if (at.includes('T')) return parseTime(at as SeriesTime).getTime();
  const timeOnly = at.match(/^(\d{1,2}):(\d{2})$/);
  if (timeOnly) {
    const { date } = productionDayOf(cursor);
    return parseTime(`${date}T${timeOnly[1]!.padStart(2, '0')}:${timeOnly[2]}`).getTime();
  }
  const dayMonth = at.match(/^(\d{1,2})\s+(\w{3})(?:\s+(\d{1,2}):(\d{2}))?$/);
  if (dayMonth) {
    const year = productionDayOf(cursor).date.slice(0, 4);
    const month = CASE_MONTH_NUM[dayMonth[2]!] ?? '06';
    const day = dayMonth[1]!.padStart(2, '0');
    const hm =
      dayMonth[3] && dayMonth[4]
        ? `${dayMonth[3].padStart(2, '0')}:${dayMonth[4]}`
        : '12:00';
    return parseTime(`${year}-${month}-${day}T${hm}`).getTime();
  }
  return null;
}

function withinTrailing24h(resolvedAt: string, cursor: SeriesTime): boolean {
  const ms = caseTimestampMs(resolvedAt, cursor);
  if (ms === null) return false;
  const cursorMs = parseTime(cursor).getTime();
  return ms >= cursorMs - 24 * 60 * 60 * 1000 && ms <= cursorMs;
}

function loopsManualPastHours(loops: readonly ControlLoopState[], hours: number): ControlLoopState[] {
  return loops.filter((loop) => {
    if (loop.mode !== 'manual') return false;
    const dwell = parseDwellHours(loop.duration);
    return dwell !== null && dwell > hours;
  });
}

function loopRow(loop: ControlLoopState): RoleScreenLoopRow {
  const dwell = parseDwellHours(loop.duration);
  const tone: PillProps['tone'] =
    loop.mode === 'auto'
      ? 'default'
      : loop.mode === 'local'
        ? 'admin'
        : dwell !== null && dwell > 4
          ? 'warning'
          : 'default';
  return {
    tag: loop.tag,
    name: loop.name,
    modeLabel: `${loop.mode.toUpperCase()} · ${loop.duration}`,
    note: loop.note,
    tone,
  };
}

function constraintChipsForCase(
  caseItem: Case,
  state: SessionState,
  advisory?: AdvisorySessionState,
): RoleScreenStatusChip[] {
  const merged = advisory
    ? applyRecommendedActions(RECOMMENDED_ACTIONS, advisory)
    : RECOMMENDED_ACTIONS;
  const actions = merged.filter(
    (row) => row.caseId === caseItem.id && row.status === 'proposed' && row.constraint,
  );
  const chips: RoleScreenStatusChip[] = [];
  for (const action of actions) {
    const chip = constraintChipForAction(action, caseItem, state);
    if (chip) chips.push({ label: chip.label, tone: 'default', title: chip.title });
  }
  return chips;
}

function caseStatusChips(
  caseItem: Case,
  advisory?: AdvisorySessionState,
  diagnosis?: Diagnosis,
  state?: SessionState,
): RoleScreenStatusChip[] {
  const chips: RoleScreenStatusChip[] = [];
  const stateChip = STATE_CHIP[caseItem.state];
  chips.push({ label: stateChip.label, tone: stateChip.tone });
  if (state) {
    chips.push(...constraintChipsForCase(caseItem, state, advisory));
  }
  const outcomeLabel = matchedRuleOutcomeLabel(caseItem, diagnosis);
  if (outcomeLabel) {
    chips.push({ label: outcomeLabel, tone: 'default' });
  }
  if (advisory) {
    const outcome = CASE_OUTCOMES.find((row) => row.caseId === caseItem.id);
    const ruleId = outcome?.proposedRuleId;
    const promoted =
      ruleId &&
      reviewsForCase(caseItem.id, advisory).some(
        (review) =>
          review.proposedChange.ruleId === ruleId &&
          review.approvalStatus === 'approved' &&
          Boolean(review.approvedBy && review.approvedAt),
      );
    if (ruleId && promoted && isRuleLive(ruleId, advisory)) {
      const version = liveRuleSet(advisory).version.n;
      chips.push({
        label: 'learned',
        tone: 'success',
        title: `learned · ${caseItem.code ?? caseItem.id} · rules v${version}`,
      });
    }
  }
  if (caseItem.branch === 'zero-touch' && caseItem.resolvedAt) {
    chips.push({ label: 'zero-touch', tone: 'default', title: ZERO_TOUCH.label });
  } else if (caseItem.branch === 'zero-touch') {
    chips.push({
      label: 'zero-touch',
      tone: 'default',
      title: ZERO_TOUCH.label,
    });
  }
  if (caseItem.evidenceRequests.some((row) => row.kind === 'lab-sample' && row.status === 'open')) {
    chips.push({ label: 'Lab sample needed', tone: 'warning' });
  }
  if (caseItem.controlModeCheck) {
    chips.push({
      label: `Loop: ${caseItem.controlModeCheck.mode.toUpperCase()}`,
      tone: caseItem.controlModeCheck.mode === 'manual' ? 'warning' : 'default',
    });
  }
  if (caseItem.samplingArtefactCheck) {
    chips.push({
      label: 'sampling artefact check',
      tone: 'warning',
      title: `${caseItem.samplingArtefactCheck.text} · lab due ${caseItem.samplingArtefactCheck.labDueAt}`,
    });
  }
  return chips;
}

function metDetailLines(caseItem: Case, reviews: readonly FMEAUpdateReview[]): { label: string; text: string }[] {
  const lines: { label: string; text: string }[] = [
    { label: 'Symptom', text: caseItem.observation },
    { label: 'Diagnosed cause', text: caseItem.causeCandidate },
    { label: 'Proposed action', text: caseItem.ifThen },
  ];
  if (caseItem.controlModeCheck) {
    lines.splice(1, 0, {
      label: 'DCS signals',
      text: `${caseItem.controlModeCheck.tag} in ${caseItem.controlModeCheck.mode.toUpperCase()}${
        caseItem.controlModeCheck.confirmedAt ? ` · confirmed ${caseItem.controlModeCheck.confirmedAt}` : ''
      }`,
    });
  }
  const labEvidence = caseItem.evidenceRequests.filter((row) => row.kind === 'lab-sample');
  if (labEvidence.length > 0) {
    lines.splice(caseItem.controlModeCheck ? 2 : 1, 0, {
      label: 'Lab signals',
      text: labEvidence.map((row) => `${row.text} (${row.status})`).join(' · '),
    });
  }
  const caseReviews = reviewsForCase(caseItem.id).filter(
    (review) => reviews.some((sessionReview) => sessionReview.id === review.id),
  );
  if (caseReviews.length > 0) {
    const review = caseReviews[0];
    lines.push({
      label: 'FMEA row status',
      text: `${review.proposedChange.summary} — ${review.reviewStatus} / ${review.approvalStatus}`,
    });
  }
  return lines;
}

function subprocessLabel(id: string): string {
  return SUBPROCESSES.find((row) => row.id === id)?.name ?? id.replace('sp-', '');
}

function threadYoursCount(members: readonly Case[], queueKind: 'met' | 'cro' | 'gm'): number {
  if (queueKind === 'met') {
    return members.filter(
      (row) =>
        row.assignedTo === 'metallurgist' || CROSS_RUNG_CASE_IDS.has(row.id),
    ).length;
  }
  if (queueKind === 'cro') {
    return members.filter(
      (row) =>
        row.assignedTo === 'cro' ||
        row.assignedTo === 'operator' ||
        CROSS_RUNG_CASE_IDS.has(row.id),
    ).length;
  }
  return members.length;
}

function quietThreadMemberChip(
  caseItem: Case,
  queueKind: 'met' | 'cro' | 'gm',
  cursor: SeriesTime,
): RoleScreenStatusChip {
  if (CLOSED_CASE_STATES.includes(caseItem.state)) {
    return { label: 'Closed', tone: 'default' };
  }
  if (queueKind === 'cro' || queueKind === 'met') {
    if (queueKind === 'cro' && !isThisShift(caseItem, cursor)) {
      return { label: 'Previous shift', tone: 'default' };
    }
    if (caseItem.branch === 'zero-touch') {
      return { label: ZERO_TOUCH.label, tone: 'default', title: ZERO_TOUCH.decode };
    }
  }
  const assignee =
    QUIET_ASSIGNEE_LABEL[caseItem.assignedTo] ?? ASSIGNEE_SHORT[caseItem.assignedTo];
  return { label: `assigned · ${assignee}`, tone: 'default' };
}

function excursionLabel(variableId: string, state: SessionState): string {
  const variable = MONITORED_VARIABLES.find((row) => row.id === variableId);
  const reading = readingByIdLookup(variable?.readingId ?? '');
  const base = reading?.label ?? variableId.replace(/^mv-/, '').toUpperCase();
  if (variable && perturbedSeriesIds(state).has(variable.seriesId)) {
    return `${base} [syn]`;
  }
  return base;
}

function queueItemsWithThreads(
  queueCases: readonly Case[],
  allCases: readonly Case[],
  input: RoleScreenInput,
  mapItem: (row: Case) => RoleScreenPrimaryItem,
  queueKind: 'met' | 'cro' | 'gm',
): RoleScreenPrimaryItem[] {
  const threads = threadsFor(allCases, input.diagnosis);
  const queueIds = new Set(queueCases.map((row) => row.id));
  const caseById = new Map(allCases.map((row) => [row.id, row]));
  const threadedIds = new Set<string>();
  const units: PrimaryListUnit[] = [];
  const seenThreadKeys = new Set<string>();

  for (const thread of threads) {
    const members = thread.caseIds
      .map((id) => caseById.get(id))
      .filter((row): row is Case => Boolean(row));
    if (members.length < 2) continue;
    const visibleOnQueue = members.filter((row) => queueIds.has(row.id));
    if (visibleOnQueue.length === 0) continue;
    const key = [...thread.caseIds].sort().join('|');
    if (seenThreadKeys.has(key)) continue;
    seenThreadKeys.add(key);

    const threadSeverity = thread.worstSeverity ?? worstSeverity(members);
    const headChip = severityChip({ severity: threadSeverity });
    const unitItems: RoleScreenPrimaryItem[] = [
      {
        caseId: thread.id,
        title: thread.title,
        threadHead: {
          rootLabel: thread.rootNodeId
            ? subprocessLabel(thread.rootNodeId)
            : thread.title,
          signalCount: thread.signalCount ?? members.length,
          yoursCount: threadYoursCount(members, queueKind),
          worstSeverity: threadSeverity,
        },
        statusChips: [{ label: headChip.label, tone: SEVERITY_PILL_TONE[threadSeverity] }],
        threadGroupId: thread.id,
        severity: threadSeverity,
      },
    ];

    for (const member of members) {
      threadedIds.add(member.id);
      if (queueIds.has(member.id)) {
        unitItems.push({
          ...mapItem(member),
          indent: true,
          threadGroupId: thread.id,
          severity: member.severity,
        });
      } else {
        unitItems.push({
          caseId: member.id,
          title: member.title,
          subtitle: member.code ? `${member.code} · ${member.circuit}` : member.circuit,
          statusChips: [quietThreadMemberChip(member, queueKind, input.sessionState.cursor)],
          indent: true,
          quiet: true,
          threadGroupId: thread.id,
          severity: member.severity,
        });
      }
    }

    units.push({ severity: threadSeverity, isThread: true, items: unitItems });
  }

  for (const caseItem of queueCases) {
    if (!threadedIds.has(caseItem.id)) {
      const item = mapItem(caseItem);
      units.push({
        severity: caseItem.severity,
        isThread: false,
        items: [{ ...item, severity: caseItem.severity }],
      });
    }
  }

  return sortQueueUnits(units).flatMap((unit) => unit.items);
}

function worstSeverity(cases: Case[]): IssueSeverity {
  const rank: Record<IssueSeverity, number> = { high: 0, med: 1, low: 2, monitor: 3 };
  return cases.reduce<IssueSeverity>((best, row) => {
    return rank[row.severity] < rank[best] ? row.severity : best;
  }, 'monitor');
}

function rankedExcursionsPanel(input: RoleScreenInput): RoleScreenSecondaryPanel {
  const allCases = mergeCasesForThreading(input.cases, input.diagnosis);
  const threads = threadsFor(allCases, input.diagnosis);
  // Alarm boundary (docs/engines/diagnosis.md § Alarm boundary): a signal a
  // seeded DCS alarm already covers is that alarm's job, not the CRO's own
  // excursion queue's — it stays on the Met feed (driftPanel, a different
  // audience), which this filter never touches. Coverage is derived per
  // alarm, gated on the alarm's own `origin: 'dcs'`
  // (DiagnosisEngine.alarmCoveredVariableIds), never a hand-listed case id.
  const alarmCovered = alarmCoveredVariableIds(ALARMS);
  const croExcursions = excursions(input.sessionState).filter(
    (event) => !alarmCovered.has(event.variableId),
  );
  const rawRanked = rankExcursions(
    croExcursions,
    input.sessionState,
    threads,
    allCases,
    input.diagnosis,
  );
  const ranked = dedupeRankedExcursions(rawRanked);
  const { shown, more } = topN(ranked, 3);
  const moreCount = more.length;
  const mapRow = (row: (typeof ranked)[number]) => {
    const workbookChip = workbookPriorityChipLabel(row.workbookPriorities);
    return {
      excursionId: row.excursion.id,
      label: excursionLabel(row.excursion.variableId, input.sessionState),
      score: row.score,
      chips: [
        ...row.reasons.map((reason) => reasonChipLabel(reason)),
        ...(workbookChip ? [workbookChip] : []),
      ],
    };
  };
  return {
    id: 'ranked-excursions',
    kind: 'ranked-excursions',
    title: 'Priority excursions',
    shown: shown.map(mapRow),
    more: more.map(mapRow),
    moreCount,
  };
}

function maintWorkListItem(caseItem: Case, advisory: AdvisorySessionState): RoleScreenPrimaryItem {
  const location = caseItem.circuit;
  const check =
    caseItem.evidenceRequests.find((row) => row.status === 'open')?.text ??
    caseItem.causeCandidate;
  return {
    caseId: caseItem.id,
    title: caseItem.title,
    subtitle: `${location} · ${check}`,
    statusChips: caseStatusChips(caseItem, advisory, undefined),
  };
}

function znRecoveryThisWeek(state: SessionState): { value: string; design?: number; provenance?: string; provenanceChip?: string } {
  const trend = recoveryTrend(state);
  const znSeries = trend.series.find((row) => row.name === 'Zn');
  if (!znSeries || znSeries.points.length === 0) {
    return { value: '—' };
  }
  const lastSeven = znSeries.points.slice(-7);
  const mean = lastSeven.reduce((sum, pt) => sum + pt, 0) / lastSeven.length;
  // The design basis the mean is read against: the Zn recovery reading's own
  // `design`, as the trend's reference line carries it (D3: no number alone).
  const design = trend.references?.find((row) => row.label.startsWith('Zn'))?.value;
  return {
    value: `${mean.toFixed(1)}%`,
    design: design && design > 0 ? design : undefined,
    provenance: 'B: slide 6 · 7-day table',
    provenanceChip: 'basis',
  };
}

/**
 * The one zero-touch share (polish.P28b, comp adjudication 6): the cases on the
 * zero-touch branch over every case in the set, so the GM's fact cell, its
 * `Zero-touch share` chart and every other reader agree (`12 of 29 · 41 %` at
 * the cursor on the Advisory role screen).
 */
export function zeroTouchShare(cases: readonly Case[]): { count: number; total: number; pct: number } {
  const count = cases.filter((row) => row.branch === 'zero-touch').length;
  const total = cases.length;
  return { count, total, pct: total === 0 ? 0 : Math.round((count / total) * 100) };
}

function recentlyClosedPanel(
  cases: readonly Case[],
  scopeFilter: (closed: Case[]) => Case[],
  cursor: SeriesTime,
): RoleScreenSecondaryPanel {
  const closed = cases.filter((row) => row.state === 'closed' && row.resolvedAt);
  const recent = closed.filter((row) => withinTrailing24h(row.resolvedAt!, cursor));
  const scoped = scopeFilter(recent)
    .slice()
    .sort(
      (a, b) =>
        (caseTimestampMs(b.resolvedAt!, cursor) ?? 0) - (caseTimestampMs(a.resolvedAt!, cursor) ?? 0),
    )
    .slice(0, 4);
  return {
    id: 'recently-closed',
    kind: 'recently-closed',
    title: 'Recently closed',
    caseIds: scoped.map((row) => row.id),
  };
}

function appendRecentlyClosed(
  secondary: RoleScreenSecondaryPanel[],
  cases: readonly Case[],
  scopeFilter: (closed: Case[]) => Case[],
  cursor: SeriesTime,
): RoleScreenSecondaryPanel[] {
  return [...secondary, recentlyClosedPanel(cases, scopeFilter, cursor)];
}

function workOrderPanelForCase(caseItem: Case): RoleScreenSecondaryPanel | undefined {
  const rows = workOrderRows();
  const wo =
    caseItem.id === 'case-p80'
      ? rows.find((row) => row.wo === 'WO-428')
      : undefined;
  if (!wo) return undefined;
  return {
    id: 'work-order',
    kind: 'work-order',
    title: 'Linked work order',
    asset: wo.asset,
    description: wo.description,
    wo: wo.wo,
    caseCode: caseItem.code,
    provenance: 'B: slide 11 · maintenance work-order table',
    provenanceChip: 'source',
  };
}

function learningLoopTrend(cases: readonly Case[]): RoleScreenSecondaryPanel {
  const { count: zeroTouch, total, pct: currentRate } = zeroTouchShare(cases);
  const months = ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'];
  const start = Math.max(45, currentRate - 25);
  const step = months.length <= 1 ? 0 : Math.round((currentRate - start) / (months.length - 1));
  const points = months.map((label, index) => ({
    label,
    value: Math.min(100, start + step * index),
  }));
  points[points.length - 1] = { label: months[months.length - 1] ?? 'Aug', value: currentRate };
  return {
    id: 'learning-trend',
    kind: 'learning-trend',
    title: ZERO_TOUCH.shareTitle,
    caption: `${zeroTouch} of ${total} seeded cases on ${ZERO_TOUCH.branchInSentence}`,
    points,
    provenance: '[syn] · interpolated from current seed mix',
  };
}

function scopeLines(scopeId: ScopeSubprocessId | null): { scopeLine: string; scopeId: ScopeSubprocessId | null } {
  if (!scopeId) {
    return { scopeLine: 'Plant-wide', scopeId: null };
  }
  return {
    scopeLine: `Scoped to ${scopeLabel(scopeId)}`,
    scopeId,
  };
}

function buildCroScreen(
  cases: readonly Case[],
  loops: readonly ControlLoopState[],
  input: RoleScreenInput,
): Pick<RoleScreenViewModel, 'kpis' | 'primary' | 'secondary' | 'footer'> {
  const croCases = cases.filter((row) => row.assignedTo === 'cro' || row.assignedTo === 'operator');
  const awaiting = croCases.filter(isOpen).length;
  const autoResolved = cases.filter(
    (row) => row.branch === 'zero-touch' && row.resolvedAt && isThisShift(row, input.sessionState.cursor),
  ).length;
  const labSamples = cases.reduce((count, row) => {
    return (
      count +
      row.evidenceRequests.filter((ev) => ev.kind === 'lab-sample' && ev.status === 'open').length
    );
  }, 0);
  const manualLoops = loopsManualPastHours(loops, 2).length;

  const shiftCases = casesForRole('cro', cases);
  const allCases = mergeCasesForThreading(cases, input.diagnosis);
  const upstreamContext = { cases: allCases, diagnosis: input.diagnosis };
  const mapCroItem = (row: Case): RoleScreenPrimaryItem => ({
    caseId: row.id,
    title: row.title,
    subtitle: row.code ? `${row.code} · ${row.circuit}` : row.circuit,
    correlationLine:
      correlationLineForCase(row, input.sessionState, upstreamContext) ?? undefined,
    statusChips: caseStatusChips(row, input.advisory, input.diagnosis, input.sessionState),
  });
  const primaryItems = queueItemsWithThreads(shiftCases, allCases, input, mapCroItem, 'cro');
  const forecast = recoveryForecastRead(input.sessionState);
  return {
    kpis: [
      {
        label: 'Awaiting my action',
        value: String(awaiting),
        caption: 'assigned to CRO / operator',
        tone: awaiting > 0 ? 'warn' : 'ok',
      },
      {
        label: ZERO_TOUCH.croKpiTile,
        value: String(autoResolved),
        caption: ZERO_TOUCH.branchClosedCaption,
        tone: 'ok',
      },
      {
        label: 'Lab samples arranged',
        value: String(labSamples),
        caption: 'open lab-sample asks company-wide',
        tone: labSamples > 0 ? 'warn' : 'ok',
      },
      {
        label: 'Loops in manual >2 h',
        value: String(manualLoops),
        caption: 'control-mode check first',
        tone: manualLoops > 0 ? 'warn' : 'ok',
      },
    ],
    primary: {
      title: 'Active cases this shift',
      items: primaryItems,
      emptyMessage: 'No active cases on this shift.',
      forecastLine: forecast.advisoryForecastLine || undefined,
    },
    secondary: appendRecentlyClosed(
      [
        rankedExcursionsPanel(input),
        {
          id: 'loops',
          kind: 'loops',
          title: 'Control loop status',
          loops: loops.map(loopRow),
        },
      ],
      cases,
      (closed) =>
        closed.filter(
          (row) =>
            row.assignedTo === 'cro' ||
            row.assignedTo === 'operator' ||
            row.id === 'case-belt-closed',
        ),
      input.sessionState.cursor,
    ),
    footer: '',
  };
}

/** Stable pattern rows collapse to one line so the panel's height is the fired rows plus one. */
function patternsStableLine(rows: ReturnType<typeof metPatternsPanel>): string | undefined {
  const stable = rows.filter((row) => row.pattern === 'stable' && row.valueLine).map((row) => row.valueLine);
  return stable.length > 0 ? `Stable · ${stable.join(' · ')}` : undefined;
}

/**
 * The FMEA reviews waiting on the Senior metallurgist seat: the Met fact
 * (`FMEA reviews awaiting me`) and the `FMEA reviews` tab headline (`3 yours`)
 * read this one list.
 */
export function seniorMetReviewsAwaiting(reviews: readonly FMEAUpdateReview[]): FMEAUpdateReview[] {
  return reviews.filter(
    (row) =>
      row.proposedChange.kind !== 'band-change' &&
      row.reviewStatus === 'pending' &&
      row.reviewSeats.includes('senior-metallurgist'),
  );
}

function buildMetScreen(
  cases: readonly Case[],
  reviews: readonly FMEAUpdateReview[],
  state: SessionState,
  input: RoleScreenInput,
): Pick<RoleScreenViewModel, 'kpis' | 'primary' | 'secondary' | 'footer'> {
  const metCases = casesForRole('metallurgist', cases, { advisoryQueue: true });
  const pendingDiagnosis = metCases.filter(
    (row) =>
      !['diagnosed', 'outcome-documented', 'fmea-review', 'approved', 'rejected'].includes(
        row.state,
      ),
  ).length;
  const fmeaAwaiting = seniorMetReviewsAwaiting(reviews).length;
  const znRecovery = znRecoveryThisWeek(state);
  const cursorMonth = state.cursor.slice(0, 7);
  const novelPatternCases = cases.filter(
    (row) =>
      row.kind === 'new-pattern' &&
      isoDateFromRaisedAt(row.raisedAt, state.cursor).startsWith(cursorMonth),
  );
  const novelPatterns = novelPatternCases.length;
  const fmeaRows: RoleScreenFmeaRow[] = reviews
    .filter(
      (row) =>
        row.proposedChange.kind !== 'band-change' &&
        row.reviewSeats.includes('senior-metallurgist'),
    )
    .map((row) => {
    const enriched = enrichFmeaReview(row, cases);
    const ruleId = row.proposedChange.ruleId;
    const occurrence = fmeaOccurrenceLabel(ruleId, cases, state, input.diagnosis);
    return {
      reviewId: row.id,
      summary: row.proposedChange.summary,
      caseCode: enriched.caseItem?.code,
      routedTo: formatReviewRoutedTo(row.reviewSeats),
      rulingStateLabel: rulingStateLabel(row, state.cursor, liveRuleSet(input.advisory).version.n),
      occurrence: occurrence.label,
      occurrenceTitle: occurrence.tooltip,
    };
  });

  const allCases = mergeCasesForThreading(cases, input.diagnosis);
  const upstreamContext = { cases: allCases, diagnosis: input.diagnosis };
  const mapMetItem = (row: Case): RoleScreenPrimaryItem => ({
    caseId: row.id,
    title: displayCaseTitle(row, state),
    subtitle: row.code ? `${row.code} · ${row.circuit}` : row.circuit,
    correlationLine: correlationLineForCase(row, state, upstreamContext) ?? undefined,
    detailLines: metDetailLines(row, reviews),
    statusChips: caseStatusChips(row, input.advisory, input.diagnosis, state),
  });

  return {
    kpis: [
      {
        label: 'Cases pending diagnosis',
        value: String(pendingDiagnosis),
        caption: ZERO_TOUCH.nonBranchCaption,
        tone: pendingDiagnosis > 0 ? 'warn' : 'ok',
      },
      {
        label: 'FMEA reviews awaiting me',
        value: String(fmeaAwaiting),
        caption: 'Senior Metallurgist seat',
        tone: fmeaAwaiting > 0 ? 'warn' : 'ok',
      },
      {
        label: 'Zn recovery this week',
        value: znRecovery.value,
        caption: znRecovery.design !== undefined
          ? `7-day mean vs ${znRecovery.design.toFixed(1)}% design`
          : '7-day mean from trend',
        tone: 'warn',
        provenance: znRecovery.provenance,
        provenanceChip: znRecovery.provenanceChip,
      },
      {
        label: 'Novel patterns this month',
        value: String(novelPatterns),
        caption: 'new-pattern cases',
        tone: novelPatterns > 0 ? 'info' : 'ok',
        linkCaseIds: novelPatternCases.map((row) => row.id),
      },
    ],
    primary: {
      title: 'Open cases',
      items: queueItemsWithThreads(metCases, allCases, input, mapMetItem, 'met'),
      emptyMessage: 'No cases awaiting metallurgical diagnosis.',
    },
    secondary: appendRecentlyClosed(
      [
        {
          id: 'patterns',
          kind: 'patterns',
          title: 'Patterns — froth, pH, residence',
          stableLine: patternsStableLine(metPatternsPanel(state, input.advisory)),
          rows: metPatternsPanel(state, input.advisory)
            .filter((row) => row.pattern !== 'stable')
            .map((row) => ({
            label: row.label,
            pattern: row.pattern,
            valueLine: row.valueLine,
            causes: row.causes,
          })),
        },
        {
          id: 'fmea-queue',
          kind: 'fmea-queue',
          title: 'FMEA update review queue',
          rows: fmeaRows,
          yoursCount: fmeaAwaiting,
        },
        {
          id: 'recovery-trend',
          kind: 'recovery-trend',
          title: 'Zn recovery trend',
          provenance: 'B: slide 6 · seeded recovery series',
          provenanceChip: 'basis',
        },
      ],
      cases,
      (closed) => closed.filter((row) => row.assignedTo === 'metallurgist'),
      state.cursor,
    ),
    footer: '',
  };
}

function buildMaintScreen(
  cases: readonly Case[],
  reviews: readonly FMEAUpdateReview[],
  advisory: AdvisorySessionState,
  sessionState: SessionState,
): Pick<RoleScreenViewModel, 'kpis' | 'primary' | 'secondary' | 'footer'> {
  const maintCases = casesForRole('maintenance', cases);
  const assignedToday = maintCases.filter((row) => isThisShift(row, sessionState.cursor)).length;
  const inspections = cases.reduce(
    (count, row) =>
      count + row.evidenceRequests.filter((ev) => ev.kind === 'visual' && ev.status === 'open').length,
    0,
  );
  const cmCrossing = conditionMonitoringPoints(sessionState).filter((row) =>
    row.statusChip === 'rising' || row.alertThreshold.toLowerCase().includes('rising'),
  ).length;

  const grindCase = maintCases.find((row) => row.id === 'case-p80');
  const workOrderPanel = grindCase ? workOrderPanelForCase(grindCase) : undefined;
  const secondary: RoleScreenSecondaryPanel[] = appendRecentlyClosed(
    workOrderPanel
      ? [workOrderPanel]
      : [
          {
            id: 'review-seat',
            kind: 'review-seat',
            title: 'Reliability review seat',
            body: `${reviews.filter((row) => row.reviewSeats.includes('senior-reliability-engineer') && row.reviewStatus === 'pending').length} mech/E/I FMEA reviews pending`,
          },
        ],
    cases,
    (closed) => closed.filter((row) => row.assignedTo === 'maintenance'),
    sessionState.cursor,
  );

  return {
    kpis: [
      {
        label: 'Mech / elec open',
        value: String(maintCases.length),
        caption: 'maintenance-assigned cases',
        tone: maintCases.length > 0 ? 'warn' : 'ok',
      },
      {
        label: 'Assigned today',
        value: String(assignedToday),
        caption: 'raised this shift',
        tone: 'info',
      },
      {
        label: 'Inspections requested',
        value: String(inspections),
        caption: 'open visual evidence asks',
        tone: inspections > 0 ? 'warn' : 'ok',
      },
      {
        label: 'Condition points crossing',
        value: String(cmCrossing),
        caption: 'CM thresholds with rising alerts',
        tone: cmCrossing > 0 ? 'warn' : 'ok',
        provenance: 'A §M9 ¶4 — condition-monitoring inventory register',
        provenanceChip: 'source',
      },
    ],
    primary: {
      title: 'Mechanical / electrical work list',
      items: maintCases.map((row) => maintWorkListItem(row, advisory)),
      emptyMessage: 'No mechanical or electrical cases assigned.',
    },
    secondary,
    footer: '',
  };
}

function buildSuperScreen(
  cases: readonly Case[],
  loops: readonly ControlLoopState[],
  _advisory: AdvisorySessionState,
  cursor: SeriesTime,
  actions: readonly RecommendedAction[] = [],
): Pick<RoleScreenViewModel, 'kpis' | 'primary' | 'secondary' | 'footer'> {
  const allOpen = casesForRole('superintendent', cases);
  const resolveRaised = (raisedAt: string) =>
    raisedAt.includes('T') ? raisedAt : clockOnProductionDay(raisedAt, cursor);

  const queueCases = allOpen
    .filter((row) => row.branch !== 'zero-touch')
    .sort(
      (a, b) =>
        diffMinutes(cursor, resolveRaised(b.raisedAt)) -
        diffMinutes(cursor, resolveRaised(a.raisedAt)),
    );
  const askCounts = new Map<string, number>();
  for (const row of queueCases) {
    const ask = superintendentResourcingAsk(row, actions);
    askCounts.set(ask, (askCounts.get(ask) ?? 0) + 1);
  }
  const oldestMinutes = queueCases.length
    ? Math.max(...queueCases.map((row) => diffMinutes(cursor, resolveRaised(row.raisedAt))))
    : 0;
  const manualPast = loopsManualPastHours(loops, 4);
  const recurring = cases.filter((row) => row.state === 'fmea-review' || row.kind === 'new-pattern').length;

  const primaryItems: RoleScreenPrimaryItem[] = queueCases.map((row) => {
    const raised = resolveRaised(row.raisedAt);
    const ageMin = diffMinutes(cursor, raised);
    const shift = shiftOf(raised);
    return {
      caseId: row.id,
      title: row.title,
      subtitle: row.handler?.name ?? row.assignedTo,
      ageLabel: formatElapsedMinutes(ageMin),
      shiftRaised: shift.kind === 'day' ? 'Day' : 'Night',
      resourcingAsk: superintendentResourcingAsk(row, actions),
      statusChips: [],
    };
  });

  const callouts: RoleScreenCallout[] = [];
  if (manualPast.length > 0) {
    callouts.push({
      title: 'Long-manual loops',
      body: `${manualPast.map((row) => row.tag).join(', ')} in MANUAL past duration threshold — judgement call for shift lead.`,
      tone: 'warn',
    });
  }
  if (recurring > 0) {
    callouts.push({
      title: 'Recurrence for CCC',
      body: `${recurring} case(s) on FMEA review / new-pattern track — worth a CCC escalation read.`,
      tone: 'info',
    });
  }

  const topAsk = [...askCounts.entries()].sort((a, b) => b[1] - a[1])[0];

  return {
    kpis: [
      {
        label: 'Open cases',
        value: String(queueCases.length),
        caption: 'non-zero-touch · plant-wide',
        tone: queueCases.length > 4 ? 'warn' : 'info',
      },
      {
        label: 'Oldest age',
        value: queueCases.length ? formatElapsedMinutes(oldestMinutes) : '—',
        caption: 'longest-open in queue',
        tone: oldestMinutes > 24 * 60 ? 'warn' : 'info',
      },
      {
        label: 'Top resourcing ask',
        value: topAsk ? topAsk[0] : '—',
        caption: topAsk ? `${topAsk[1]} case(s)` : 'no open cases',
        tone: 'info',
      },
      {
        label: 'Ask kinds',
        value: String(askCounts.size),
        caption: 'distinct resourcing decisions',
        tone: askCounts.size > 2 ? 'warn' : 'ok',
      },
    ],
    primary: {
      title: 'Management queue',
      items: primaryItems,
      emptyMessage: 'No open cases need superintendent resourcing.',
    },
    secondary: appendRecentlyClosed(
      [
        {
          id: 'callouts',
          kind: 'callouts',
          title: 'Shift decisions',
          callouts,
        },
      ],
      cases,
      (closed) => closed,
      cursor,
    ),
    footer: '',
  };
}

function caseHasReviewInFlight(
  caseItem: Case,
  reviews: readonly FMEAUpdateReview[],
): boolean {
  const outcome = CASE_OUTCOMES.find((row) => row.caseId === caseItem.id);
  if (!outcome) return false;
  return reviews
    .filter((row) => row.caseOutcomeId === outcome.id)
    .some(
      (row) =>
        row.reviewStatus === 'pending' ||
        (row.reviewStatus === 'approved' && row.approvalStatus === 'pending'),
    );
}

function buildManagerScreen(
  cases: readonly Case[],
  reviews: readonly FMEAUpdateReview[],
  input: RoleScreenInput,
): Pick<RoleScreenViewModel, 'kpis' | 'primary' | 'secondary' | 'footer'> {
  const awaitingSignOff = reviews.filter(
    (row) => row.reviewStatus === 'approved' && row.approvalStatus === 'pending',
  );
  const zeroTouch = zeroTouchShare(cases);
  const casesThisMonth = cases.filter((row) =>
    isCaseThisMonth(row.raisedAt, input.sessionState.cursor),
  ).length;
  const fmeaLive = FAILURE_MODES.filter((row) => row.status === 'live').length;

  const signOffRows: RoleScreenSignOffRow[] = awaitingSignOff.map((row) => {
    const outcome = CASE_OUTCOMES.find((item) => item.id === row.caseOutcomeId);
    const caseItem = outcome ? cases.find((item) => item.id === outcome.caseId) : undefined;
    return {
      reviewId: row.id,
      summary: row.proposedChange.summary,
      caseTitle: caseItem?.title,
      caseId: caseItem?.id,
      reviewedBy: row.reviewedBy,
      approvalStatus: row.approvalStatus,
    };
  });

  const managerCases = casesForRole('gm', cases, { reviews });
  const reviewCases = managerCases.filter((row) => caseHasReviewInFlight(row, reviews));
  const reviewCaseIds = new Set(reviewCases.map((row) => row.id));
  const allCases = mergeCasesForThreading(cases, input.diagnosis);
  const mapManagerItem = (row: Case): RoleScreenPrimaryItem => ({
    caseId: row.id,
    title: row.title,
    subtitle: row.code ? `${row.code} · ${row.circuit}` : row.circuit,
    statusChips: caseStatusChips(row, input.advisory, input.diagnosis),
    group: 'Cases in review',
  });
  const primaryItems = queueItemsWithThreads(
    reviewCases,
    allCases,
    input,
    mapManagerItem,
    'gm',
  ).map((item) =>
    item.quiet && !reviewCaseIds.has(item.caseId)
      ? { ...item, group: 'Thread members' }
      : item,
  );

  const signOffPanel: RoleScreenSecondaryPanel = {
    id: 'sign-off',
    kind: 'sign-off',
    title: 'Sign-off gate',
    rows: signOffRows,
  };

  return {
    kpis: [
      {
        label: 'Reviews awaiting sign-off',
        value: String(awaitingSignOff.length),
        caption: 'reviewed · pending Plant Manager gate',
        tone: awaitingSignOff.length > 0 ? 'warn' : 'ok',
      },
      {
        label: ZERO_TOUCH.kpiTile,
        value: `${zeroTouch.pct}%`,
        caption: `${zeroTouch.count} of ${zeroTouch.total} seeded cases`,
        tone: 'ok',
      },
      {
        label: 'Cases this month',
        value: String(casesThisMonth),
        caption: 'Month 4 standing + shift cases',
        tone: 'info',
      },
      {
        label: 'FMEA rows live',
        value: String(fmeaLive),
        caption: 'approved failure-mode rows',
        tone: 'ok',
      },
    ],
    primary: {
      title: 'Cases in review',
      items: primaryItems,
      emptyMessage: 'No cases with FMEA reviews in flight.',
    },
    secondary: appendRecentlyClosed(
      [signOffPanel, learningLoopTrend(cases)],
      cases,
      (closed) => closed,
      input.sessionState.cursor,
    ),
    footer: '',
  };
}

export function roleScreen(
  id: RoleScreenId,
  input: RoleScreenInput,
): RoleScreenViewModel {
  const scopeId = parseScopeParam(input.scope ?? null);
  const scopedCases = filterCasesByScope(input.cases, scopeId);
  const queueInput = input;
  const meta = SCREEN_META[id];
  const { scopeLine } = scopeLines(scopeId);
  const diagnosis = input.diagnosis;

  const body =
    id === 'cro'
      ? buildCroScreen(scopedCases, input.loops, { ...queueInput, diagnosis })
      : id === 'met'
        ? buildMetScreen(scopedCases, input.reviews, input.sessionState, { ...queueInput, diagnosis })
        : id === 'maint'
          ? buildMaintScreen(scopedCases, input.reviews, input.advisory, input.sessionState)
          : id === 'super'
            ? buildSuperScreen(
                scopedCases,
                input.loops,
                input.advisory,
                input.sessionState.cursor,
                input.actions,
              )
            : buildManagerScreen(scopedCases, input.reviews, { ...queueInput, diagnosis });

  return {
    screenId: id,
    badge: meta.badge,
    badgeTone: meta.badgeTone,
    title: meta.title,
    question: meta.question,
    scopeLine,
    scopeId,
    cadenceLine: meta.cadence,
    kpis: body.kpis,
    primary: body.primary,
    secondary: body.secondary,
    footer: body.footer,
  };
}

// ---------------------------------------------------------------------------
// The role-screen card (polish.P28b, comp `advisory-stages.html` family A):
// the persona's secondary panels become the card's ink pill tabs, each label
// carrying its headline so the tab reads before it is opened.
// ---------------------------------------------------------------------------

export type RoleScreenTab = {
  id: string;
  label: string;
  /** The tab's headline beside its label (`1 cyclical`, `11`, `41%`); empty when it has none. */
  headline: string;
  panel: RoleScreenSecondaryPanel;
};

function tabHeadline(panel: RoleScreenSecondaryPanel): string {
  switch (panel.kind) {
    case 'patterns':
      if (panel.rows.length === 0) return '';
      return panel.rows.length === 1 ? `1 ${panel.rows[0]!.pattern.toLowerCase()}` : `${panel.rows.length} fired`;
    case 'fmea-queue':
      return panel.yoursCount !== undefined ? `${panel.yoursCount} yours` : String(panel.rows.length);
    case 'recently-closed':
      return String(panel.caseIds.length);
    case 'sign-off':
      return String(panel.rows.length);
    case 'learning-trend': {
      const last = panel.points[panel.points.length - 1];
      return last ? `${last.value}%` : '';
    }
    case 'loops':
      return String(panel.loops.length);
    case 'ranked-excursions':
      return String(panel.shown.length + panel.moreCount);
    case 'callouts':
      return panel.callouts.length > 0 ? String(panel.callouts.length) : '';
    case 'work-order':
      // The work order's id is the body's first line; the tab keeps its short label.
      return '';
    default:
      return '';
  }
}

// The tab row shares the card's 5/12 column and sits on one line at 1470 for
// every persona (fix-p28b-r1b): `Zn recovery 7 d` is the comp's own label, and
// `Closed` stands for `Recently closed`, whose panel keeps the full title.
const TAB_LABEL: Partial<Record<RoleScreenSecondaryPanel['kind'], string>> = {
  patterns: 'Patterns',
  'fmea-queue': 'FMEA reviews',
  'recently-closed': 'Closed',
  'recovery-trend': 'Zn recovery 7 d',
  'sign-off': 'Sign-off gate',
  'learning-trend': 'Zero-touch share',
  loops: 'Control loops',
  'work-order': 'Work order',
};

/** The role card's tabs, in the screen's own panel order. */
export function roleScreenTabs(model: Pick<RoleScreenViewModel, 'secondary'>): RoleScreenTab[] {
  return model.secondary.map((panel) => ({
    id: panel.id,
    label: TAB_LABEL[panel.kind] ?? panel.title,
    headline: tabHeadline(panel),
    panel,
  }));
}

// ---------------------------------------------------------------------------
// Tab charts (polish.P28b r1): a series with no state draws in ink and its
// design basis as a neutral dashed line (comp `spark`: ink line, ink-tertiary
// reference); state colour stays for what is itself out of state.
// ---------------------------------------------------------------------------

const INK_SERIES = 'var(--ink-primary)';
const NEUTRAL_REFERENCE = 'var(--ink-tertiary)';

export type RoleScreenTabChart = {
  categories: readonly string[];
  series: readonly TrendSeries[];
  references: readonly TrendReference[];
};

/** The Met `Zn recovery trend` tab: the Zn series in ink over its design line. */
export function roleScreenRecoveryChart(state: SessionState): RoleScreenTabChart {
  const recovery = recoveryTrend(state);
  return {
    categories: recovery.categories,
    series: recovery.series
      .filter((row) => row.name === 'Zn')
      .map((row) => ({ ...row, tone: 'neutral' as const, color: INK_SERIES })),
    references: (recovery.references ?? [])
      .filter((row) => row.label.startsWith('Zn'))
      .map((row) => ({ ...row, tone: 'neutral' as const, color: NEUTRAL_REFERENCE })),
  };
}

/** The GM `Zero-touch share` tab: the monthly share as an ink line. */
export function roleScreenZeroTouchChart(
  panel: Extract<RoleScreenSecondaryPanel, { kind: 'learning-trend' }>,
): RoleScreenTabChart {
  return {
    categories: panel.points.map((row) => row.label),
    series: [{
      name: ZERO_TOUCH.shareTitle,
      points: panel.points.map((row) => row.value),
      tone: 'neutral',
      color: INK_SERIES,
    }],
    references: [],
  };
}
